import { Colors } from "@constants"
import { NavigationProp } from "@react-navigation/core"
import React, { useEffect, useState, useRef } from "react"
import { View, Text, ScrollView, ViewStyle, TextStyle } from "react-native"
import { useDevices } from "@hooks"
import { useDispatch, useSelector } from "react-redux"
import { GlobalState } from "@redux"
import { FloatTextInput } from "@atomic"
import { InfoBox } from "@atomic/molecules/InfoBox"
import { InfoList } from "@atomic"
import { I18n } from "react-redux-i18n"
import { Title18, Title13 } from "@stylesheets"
import { Button } from "@modules/profile/screens/SettingsScreen/MfaSetup/Button"
import { BadgeFormStyles } from "./styles"
import {
  EstablishmentSelectionStyles,
  inputContainerStyles,
} from "../EstablishmentSelection/styles"
import { AccountActions, BadgeLayouts, HoldingAdditionErrors } from "@modules"
import useBadgeManagement from "@hooks/useBadgeManagement"
import { isEqual } from "lodash"

type BadgeFormProps = {
  navigation: NavigationProp<any>;
  layoutHandler?: (event: any, key: BadgeLayouts) => void;
  mainRef?: React.RefObject<ScrollView>;
  setPreferredZoneData: any;
  preferredZoneData: {
    selectedPrefZones: any[];
    hasPrefZonesError: boolean;
  };
  setHoldingViewAlias: React.Dispatch<React.SetStateAction<string>>
  holdingViewAlias: string;
}

const textInputStyles = {
  textInputContainer: { width: "100%", margin: 0 } as TextStyle,
  mainContainer: { width: "100%", alignItems: "center" } as ViewStyle,
  textInputStyle: { width: "100%" } as ViewStyle,
}

export const BadgeForm = React.forwardRef<View, BadgeFormProps>(
  ({ navigation, layoutHandler, mainRef, setPreferredZoneData, preferredZoneData, holdingViewAlias, setHoldingViewAlias }, ref) => {
    const [isMobile] = useDevices()
    const { selectedHolding, isEditionMode, editionHoldingView } = useSelector(
      (state: GlobalState) => state.account
    )

    const userInfo = useSelector((state: GlobalState) => state.auth.userInfo)

    const [serialNumber, setSerialNumber] = useState<string>("")
    const [serialNumberEditingFinished, setSerialNumberEditingFinished] = useState<boolean>(false);
    const [badgeName, setBadgeName] = useState<string>("")
    const [guestLastNameEditingFinished, setGuestLastNameEditingFinished] = useState<boolean>(false);
    const [haveBadgeData, setHaveBadgeData] = useState<boolean>(false)
    const [showBadgeError, setShowBadgeError] = useState<boolean>(false)

    const [showSecurityError, setShowSecurityError] = useState<boolean>(false)
    const [securityCode, setSecurityCode] = useState<string>("")
    const [showError, setShowError] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>("")

    const [serialNumberError, setSerialNumberError] = useState<string>("")
    const [securityCodeError, setSecurityCodeError] = useState<string>("")

    const serialNumberRef = useRef<View>(null)
    const securityCodeRef = useRef<View>(null)

    const styles = BadgeFormStyles(isMobile)
    const stylesStep1 = EstablishmentSelectionStyles(isMobile)

    const dispatch = useDispatch()

    const { sendBadge } = useBadgeManagement()

    const onChangeBadgeNumber = (text: string) => {
      setShowBadgeError(false)
      setSerialNumberError("")
      setSerialNumberEditingFinished(false);
      if (text !== "" && badgeName !== "") {
        setHaveBadgeData(true)
      } else if (isEditionMode && text === "" && badgeName === "") {
        setHaveBadgeData(true);
      } else {
        setHaveBadgeData(false)
      }
      setSerialNumber(text)
    }

    const onChangeBadgeName = (text: string) => {
      setShowBadgeError(false)
      setSerialNumberError("")
      setGuestLastNameEditingFinished(false);
      if (text !== "" && serialNumber !== "") {
        setHaveBadgeData(true)
      } else if (isEditionMode && text === "" && serialNumber === "") {
        setHaveBadgeData(true);
      } else {
        setHaveBadgeData(false)
      }
      setBadgeName(text)
    }

    const onSecurityCodeChange = (text: string) => {
      setShowSecurityError(false)
      setSecurityCodeError("")
      setSecurityCode(text)
    }

    const scrollTo = (ref: any) => {
      ref.current?.scrollIntoView({ behavior: "smooth" })
    }

    const onSecondaryPress = async (skipped = false) => {
      if (skipped) {
        onServicesActivation(skipped)
        return
      }
      await dispatch(AccountActions.setHasBadge(false))
    }

    const onServicesActivation = async (skipped = false) => {
      setShowError(false)
      setErrorMessage("")
      setShowBadgeError(false)
      setSerialNumberError("")
      setShowSecurityError(false)
      setSecurityCodeError("")
      setSerialNumberEditingFinished(false);
      setGuestLastNameEditingFinished(false);

      let data = {
        idHolding: selectedHolding!.id,
        alias: holdingViewAlias.trim() || "",
        serialNumber: skipped ? "" : serialNumber.trim(),
        guestLastName: skipped ? "" : badgeName.trim(),
        guestValidationCode: skipped ? "" : securityCode.trim(),
        preferredZones: selectedHolding?.enabledPreferredZones
          ? preferredZoneData.selectedPrefZones.map((zone) => ({
              id: String(zone.id) as string,
              name: zone.name as string,
            }))
          : undefined,
      }

      try {
        await sendBadge(data, BadgeLayouts.BadgeForm, skipped)
      } catch (error) {
        switch (error) {
          case HoldingAdditionErrors.BADGE_NOT_FOUND:
            setShowBadgeError(true)
            setSerialNumberError(
              I18n.t(`associationBadge.badgeForm.incorrectBadge`)
            )
            scrollTo(serialNumberRef)
            break
          case HoldingAdditionErrors.BADGE_EXPIRED:
          case HoldingAdditionErrors.GUEST_MUST_BE_ENABLED: 
            setShowBadgeError(true)
            setSerialNumberError(
              I18n.t(`account.expiredBadge`)
            )
            scrollTo(serialNumberRef)
            break
          case HoldingAdditionErrors.SECURITY_BADGE_CODE_INVALID:
            setShowSecurityError(true)
            setSecurityCodeError(
              I18n.t(`associationBadge.badgeForm.invalidSecurityCode`)
            )
            scrollTo(securityCodeRef)
            break
          case HoldingAdditionErrors.SECURITY_BADGE_CODE_REQUIRED:
            setShowSecurityError(true)
            setSecurityCodeError(
              I18n.t(`associationBadge.badgeForm.badgeAlreadyAssociated`)
            )
            scrollTo(securityCodeRef)
            break
          case HoldingAdditionErrors.SCOLAPASS_BADGE_NOT_FOUND:
            setShowBadgeError(true)
            setSerialNumberError(
              I18n.t(`associationBadge.badgeForm.incorrectBadgeScolapass`)
            )
            scrollTo(serialNumberRef)
            break
          default:
            setShowError(true)
            setErrorMessage(I18n.t("associationBadge.globalError"))
            mainRef?.current?.scrollToEnd({ animated: true })
            break
        }
      }
    }

    const checkBadgeInfo =
      (!serialNumber && !!badgeName) || (!!serialNumber && !badgeName)

    const checkPrefZones =
      preferredZoneData.hasPrefZonesError &&
      selectedHolding?.enabledPreferredZones;

    const scholaPassVariantI18n = selectedHolding?.isScolaPassCashSystem ? 1 : 0

    const infoListItems = scholaPassVariantI18n ? [
      I18n.t(`associationBadge.badgeForm.infoBadgeDescriptionScolapass.0`),
      I18n.t(`associationBadge.badgeForm.infoBadgeDescriptionScolapass.1`),
    ] : [
      I18n.t(`associationBadge.badgeForm.infoBadgeDescription.0`),
      I18n.t(`associationBadge.badgeForm.infoBadgeDescription.1`),
      I18n.t(`associationBadge.badgeForm.infoBadgeDescription.2`),
      I18n.t(`associationBadge.badgeForm.infoBadgeDescription.3`),
    ]

    const securityInfoListItems = scholaPassVariantI18n ? [
      I18n.t(`associationBadge.badgeForm.infoSecurityDescriptionScolapass.0`),
      I18n.t(`associationBadge.badgeForm.infoSecurityDescriptionScolapass.1`),
      I18n.t(`associationBadge.badgeForm.infoSecurityDescriptionScolapass.2`),
    ] :[
      I18n.t(`associationBadge.badgeForm.infoSecurityDescription.0`),
      I18n.t(`associationBadge.badgeForm.infoSecurityDescription.1`),
      I18n.t(`associationBadge.badgeForm.infoSecurityDescription.2`),
    ]

    const currentSerialNumber = editionHoldingView?.guest?.supportSerialNumber || editionHoldingView?.guest?.serialNumber || ""
    const currentLastName = editionHoldingView?.guest?.lastName || ""
    const hasBadgeDataChanged = currentSerialNumber !== serialNumber.trim() || currentLastName !== badgeName.trim()

    const isUnchanged = () => {
      if (!isEditionMode || !editionHoldingView) {
        return false
      }

      const isSameName =
        badgeName.trim() === (editionHoldingView.guest?.lastName || "")
      const isSameSerialNumber = serialNumber.trim() === currentSerialNumber
      const isSameAlias = holdingViewAlias.trim() === (editionHoldingView.alias || "");

      const originalZones = editionHoldingView.preferredZones || []
      const currentZones = preferredZoneData.selectedPrefZones || []
      const isSameZones = isEqual(
        originalZones.map((z) => z.id).sort(),
        currentZones.map((z) => z.id).sort()
      )

      return isSameName && isSameSerialNumber && isSameAlias && isSameZones
    }

    useEffect(() => {
      if (
        isEditionMode &&
        editionHoldingView &&
        editionHoldingView?.holding?.id === selectedHolding?.id
      ) {
        setSerialNumber(currentSerialNumber)
        setBadgeName(editionHoldingView.guest?.lastName || "")
        setHoldingViewAlias(editionHoldingView.alias || "");
        setPreferredZoneData({
          hasPrefZonesError: editionHoldingView?.preferredZones?.length === 0,
          selectedPrefZones: editionHoldingView?.preferredZones || [],
        })
        setHaveBadgeData(true)
      }
      else if (
        isEditionMode &&
        editionHoldingView &&
        editionHoldingView?.holding?.id !== selectedHolding?.id
      ) {
        setSerialNumber("");
        setBadgeName("");
        setHoldingViewAlias(editionHoldingView.alias || "");
        setPreferredZoneData({
          hasPrefZonesError: true,
          selectedPrefZones: [],
        });
        setHaveBadgeData(true);
      }
      else if (userInfo?.lastName && badgeName === "") {
        setBadgeName(userInfo.lastName)
      }
    }, [isEditionMode, editionHoldingView, userInfo, currentSerialNumber])

    const onBackPress = () => {
      navigation?.goBack();
    }

    const onSubmit = async () => {
      if (isEditionMode && !serialNumber && !badgeName) {
        await onSecondaryPress(!selectedHolding?.isProtected);
        return;
      }
      await onServicesActivation();
    }

    return (
      <>
        <View
          onLayout={(e) => layoutHandler?.(e, BadgeLayouts.BadgeForm)}
          ref={ref}
          style={stylesStep1.container}
        >
          <View style={stylesStep1.listContainer}>
            <Title18 isBold style={styles.titleText}>
              {I18n.t(
                `associationBadge.badgeForm.badgeNumberTitle.${scholaPassVariantI18n}`
              )}
            </Title18>

            <Title13 style={styles.fieldLabel} ref={serialNumberRef}>
              {I18n.t(
                `associationBadge.badgeForm.badgeNumberLabel.${scholaPassVariantI18n}`
              )}
            </Title13>
            <View style={styles.textHoldingInputContainer}>
              <FloatTextInput
                value={serialNumber}
                disabled={false}
                onChangeValue={onChangeBadgeNumber}
                textPlaceHolder='Ex: AX2541'
                hasError={(serialNumberEditingFinished && checkBadgeInfo) || showBadgeError}
                errorMessage={serialNumberError}
                containerStyles={inputContainerStyles}
                onBlur={() => {
                  setSerialNumberEditingFinished(true);
                }}
              />
            </View>
            <InfoBox
              type='info'
              title={
                <Text style={stylesStep1.infoBoxTitle}>
                  {I18n.t(`associationBadge.badgeForm.infoBadgeTitle.${scholaPassVariantI18n}`)}
                </Text>
              }
              collapsible
              defaultOpen={false}
            >
              <InfoList items={infoListItems} bulletColor={Colors.darkGrey} />
            </InfoBox>
            <Title13 style={styles.fieldLabel}>
              {I18n.t(`associationBadge.badgeForm.badgeNameLabel`)}
            </Title13>
            <View style={styles.textHoldingInputContainer}>
              <FloatTextInput
                value={badgeName}
                onChangeValue={onChangeBadgeName}
                textPlaceHolder='Ex: Dupont'
                hasError={(guestLastNameEditingFinished && checkBadgeInfo) || showBadgeError}
                containerStyles={inputContainerStyles}
                onBlur={() => {
                  setGuestLastNameEditingFinished(true);
                }}
              />
            </View>
          </View>
          <View style={styles.badgePersonalizationContainer}>
            <Title13 style={styles.fieldLabel}>
              {I18n.t(
                `associationBadge.badgeForm.badgePersonalizationLabel.${scholaPassVariantI18n}`
              )}
              <Text style={styles.optionalText}>
                {I18n.t(`associationBadge.badgeForm.optionalLabel`)}
              </Text>
            </Title13>
            <Text style={styles.badgePersonalizationSubtitle}>
              {I18n.t(
                `associationBadge.badgeForm.badgePersonalizationDescription.${scholaPassVariantI18n}`
              )}
            </Text>
            <View style={styles.textHoldingInputContainer}>
              <FloatTextInput
                value={holdingViewAlias}
                onChangeValue={setHoldingViewAlias}
                textPlaceHolder={I18n.t(
                  `associationBadge.badgeForm.badgePersonalizationPlaceholder.${scholaPassVariantI18n}`
                )}
                containerStyles={textInputStyles}
              />
            </View>
          </View>
          {(!isEditionMode || hasBadgeDataChanged) && <View style={styles.badgePersonalizationContainer}>
            <Title13 style={styles.fieldLabel} ref={securityCodeRef}>
              {I18n.t(`associationBadge.badgeForm.securityCodeLabel`)}
              <Text style={styles.optionalText}>
                {I18n.t(`associationBadge.badgeForm.optionalLabel`)}
              </Text>
            </Title13>
            <Text style={styles.badgePersonalizationSubtitle}>
              {I18n.t(
                `associationBadge.badgeForm.securityCodeDescription.${scholaPassVariantI18n}`
              )}
            </Text>
            <View style={styles.textHoldingInputContainer}>
              <FloatTextInput
                value={securityCode}
                onChangeValue={onSecurityCodeChange}
                textPlaceHolder='Ex: 1234567'
                hasError={showSecurityError}
                errorMessage={securityCodeError}
                containerStyles={inputContainerStyles}
              />
            </View>
            <InfoBox
              type='info'
              title={
                <Text style={stylesStep1.infoBoxTitle}>
                  {I18n.t(`associationBadge.badgeForm.infoSecurityCodeTitle.${scholaPassVariantI18n}`)}
                </Text>
              }
              collapsible
              defaultOpen={false}
            >
              <InfoList
                items={securityInfoListItems}
                bulletColor={Colors.transparent}
              />
            </InfoBox>
            {showError && (
              <InfoBox
                type='danger'
                title={
                  <Text style={stylesStep1.infoBoxTitle}>{errorMessage}</Text>
                }
              />
            )}
          </View>}
          <View style={styles.buttonsContainer}>
            {isEditionMode ? (
              <Button
                type='secondary'
                onPress={() =>
                  onBackPress()
                }
              >
                <Text>{I18n.t(`common.return`)}</Text>
              </Button>
            ): (
              <Button
                type='secondary'
                onPress={() =>
                  onSecondaryPress(selectedHolding?.isProtected ? false : true)
                }
              >
                <Text>
                  {I18n.t(
                    `associationBadge.badgeForm.haveCode.${scholaPassVariantI18n}`
                  )}
                </Text>
              </Button>
            )}
            <Button
              type='primary'
              disabled={!haveBadgeData || checkPrefZones || isUnchanged()}
              onPress={onSubmit}
            >
              <Text>
                {isEditionMode ? I18n.t(`associationBadge.badgeForm.saveModifications`) : I18n.t(`associationBadge.badgeForm.activateServices`)}
              </Text>
            </Button>
          </View>
        </View>
      </>
    )
  }
)
